/**
 * /app/plant-time: the plant's time, for every org (2026-10-01). Its zone, when its production day starts (on its own
 * date, or the evening before, counting toward the next date), its week start and its shift calendar: what every day,
 * shift and week on the platform is cut on. Until now it could only be set by an API call; the briefs, the reports and
 * the analysis read it, and a plant with no calendar is never split by shift.
 *
 * An org admin edits; every other member reads (the PUT is org admins'). A save sends only what changed
 * (lib/plant-time-form changeOf), so editing the day start never wipes the shift calendar. A live preview shows
 * today's shifts on the plant's clock. The view is pure (rendered in tests without providers); the page at the bottom
 * holds the hooks.
 */
import { useState } from "react";
import { Clock, Loader2, Save } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { EmptyState } from "@/lib/ui/empty-state";
import { useOrg } from "@/lib/org-context";
import { useBriefLive } from "@/lib/api/brief";
import { usePlantTimeQuery, useSavePlantTime } from "@/lib/api/plant-time";
import { PlantTimeFields, TodaysShiftsPreview } from "@/components/plant-time/PlantTimeFields";
import {
  browserZone,
  changeOf,
  clockChangeWords,
  formOf,
  formProblems,
  plantTimeSaveErrorText,
  SHIFT_ENDS_WARNING,
  shiftEndsMissing,
  storedDayStartNote,
  timeZoneOptions,
  type PlantTimeForm,
} from "@/lib/plant-time-form";
import type { PlantTime } from "@/lib/shift-calendar";

export interface PlantTimeViewProps {
  /** The plant time as stored. */
  stored: PlantTime;
  /** The form as edited (formOf(stored) until someone edits). */
  form: PlantTimeForm;
  /** The browser's zone, offered as a suggestion to a plant that has none; never filled in. */
  browser: string;
  zones: string[];
  canEdit: boolean;
  now: Date;
  saving?: boolean;
  saved?: boolean;
  error?: string | null;
  onChange?: (next: PlantTimeForm) => void;
  onSave?: () => void;
  onReset?: () => void;
}

export function PlantTimeView({
  stored,
  form,
  browser,
  zones,
  canEdit,
  now,
  saving = false,
  saved = false,
  error = null,
  onChange,
  onSave,
  onReset,
}: PlantTimeViewProps) {
  const change = changeOf(stored, form);
  const changed = Object.keys(change).length > 0;
  const problems = canEdit ? formProblems(form) : [];
  const unsetZone = !stored.timezone.trim();
  const staleDayStart = storedDayStartNote(stored);
  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-3xl text-sm text-on-surface-muted">
        Every day, shift and week on the platform is cut on the plant&apos;s own clock: its zone, when its production day
        starts, and its shifts. Imports read times on it, and the briefs and reports name days and shifts by it.{" "}
        {clockChangeWords()}
      </p>
      {unsetZone && (
        <p role="note" className="max-w-3xl text-sm text-on-surface">
          The plant has no time zone yet, so its days are UTC days.
          {canEdit ? " Choose the plant's zone below; nothing is saved until you do." : ""}
        </p>
      )}
      {staleDayStart && (
        <p role="note" className="max-w-3xl text-sm text-on-surface">
          {staleDayStart}
        </p>
      )}
      {shiftEndsMissing(form.shifts) && (
        <p role="note" className="max-w-3xl rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-on-surface">
          {SHIFT_ENDS_WARNING} Until every shift has an end, they fetch at any time, slowly.
        </p>
      )}
      {!canEdit && (
        <p className="text-sm text-on-surface-muted">Your role can read the plant time; an org admin sets it.</p>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <PlantTimeFields
          form={form}
          onChange={(f) => onChange?.(f)}
          zones={zones}
          disabled={!canEdit || saving}
          suggestedZone={canEdit ? browser : undefined}
        />
        <TodaysShiftsPreview form={form} now={now} />
      </div>
      {problems.length > 0 && changed && (
        <ul role="alert" className="flex list-disc flex-col gap-1 pl-5 text-sm text-danger">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {saved && !changed && !error && (
            <span role="status" className="text-sm text-on-surface-muted">
              Saved. The analysis is refreshed with the new plant time.
            </span>
          )}
          {!changed && !saved && <span className="text-sm text-on-surface-muted">Nothing to save.</span>}
          <Button type="button" variant="ghost" size="sm" onClick={onReset} disabled={!changed || saving}>
            Undo changes
          </Button>
          <Button type="button" size="sm" onClick={onSave} disabled={!changed || problems.length > 0 || saving}>
            {saving ? <Loader2 className="animate-spin" aria-hidden /> : <Save aria-hidden />}
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

/** The plant's time and shifts, read and set: the Plant time tab of Settings, and the page's body. */
export function PlantTimeSettings() {
  const live = useBriefLive();
  const { can } = useOrg();
  const read = usePlantTimeQuery();
  const save = useSavePlantTime();
  const browser = browserZone();
  const [edited, setEdited] = useState<PlantTimeForm | null>(null);

  let body: React.ReactNode;
  if (!live) {
    body = (
      <EmptyState
        icon={Clock}
        title="The plant's time lives on the platform."
        description="Sign in to the live platform to read and set it."
      />
    );
  } else if (read.isLoading) {
    body = <p className="text-sm text-on-surface-muted">Reading the plant time.</p>;
  } else if (read.isError || !read.data) {
    body = (
      <p role="alert" className="text-sm text-danger">
        The plant time could not be read. Try again in a moment.
      </p>
    );
  } else {
    const stored = read.data;
    const form = edited ?? formOf(stored);
    body = (
      <PlantTimeView
        stored={stored}
        form={form}
        browser={browser}
        zones={timeZoneOptions([form.zone, stored.timezone, browser])}
        canEdit={can("org.admin")}
        now={new Date()}
        saving={save.isPending}
        saved={save.isSuccess}
        error={save.isError ? plantTimeSaveErrorText(save.error) : null}
        onChange={(f) => {
          save.reset();
          setEdited(f);
        }}
        onSave={() => save.mutate(changeOf(stored, form), { onSuccess: () => setEdited(null) })}
        onReset={() => {
          save.reset();
          setEdited(null);
        }}
      />
    );
  }
  return <>{body}</>;
}
